// Slider component for rating selections.
// Slider component with discrete stop points. ZDF FORK
import { t, undefN } from "util/stdtypes";
import { HorizBox, Pad, PadBox } from 'component/basics'
import { colorAmbiguousWhite, colorBorderPrimary, colorButtonAccent, colorButtonSecondary, colorGreyHover, colorSurfaceSecondary } from 'component/color'
import { useEffect, useRef, useState } from 'react';
import { Animated, PanResponder, Pressable, StyleSheet, View } from 'react-native';
import { DataVizText, UtilityText } from 'component/text'
import { useTranslation } from 'component/translation';
import { NonSelectableView } from "../platform-specific/non-selectable-view";

export function RatingSelectorReworked({value, defaultValue=undefN, optionLabels, optionColors, placeholder, onChangeValue}) {
    const s = RatingSelectorReworkedStyle;
    const sideOne = optionLabels[0];
    const sideTwo = optionLabels[4];
    const sideMiddle = optionLabels[2];    
    const [hasStartedInteraction, setHasStartedInteraction] = useState(false);

    return (
        <View style={s.outer}>
            <PadBox horiz={20}>
                <SliderWithStopPointsRestyled
                    title={placeholder}
                    numberOfStops={5}
                    value={value || defaultValue}
                    onChangeValue={(v) => {
                        onChangeValue(v);
                    }}
                    optionLabels={optionLabels}
                    onStartInteraction={() => {
                        setHasStartedInteraction(true);
                    }}
                    onEndInteraction={() => {

                    }}
                    initial={!value}
                />
            </PadBox>

            {!value && (
                <HorizBox spread>
                    <PadBox left={20}>
                        <SelectionLabel label={sideOne} />
                    </PadBox>
                    <PadBox horiz={4}>
                        <SelectionLabel label={sideMiddle} />
                    </PadBox>
                    <PadBox right={20}>
                        <SelectionLabel label={sideTwo} />
                    </PadBox>
                </HorizBox>
            )}
        </View>
    );
}

const RatingSelectorReworkedStyle = StyleSheet.create({
    outer: {
        borderRadius: 8,
        paddingBottom: 40,
    }
})

function SelectionLabel({ label }) {
    return (
        <HorizBox center>
            <UtilityText weight="strong" type="tiny" label={t(label)} isNonSelectable />
        </HorizBox>
    );
}


// A slider component that snaps to the nearest stop point when released.
// A value of zero means that nothing has been selected, and the handle will be hidden.

// HACK: This component requires a few casts to any to pass typechecking
// At some point we should clean this up so it doesn't need that.

// HACK: This will only work on web. Further work will be needed if we want to support native.

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function SliderWithStopPointsRestyled({title, numberOfStops, optionLabels, value, onChangeValue, onStartInteraction, onEndInteraction, initial=false}) {
    const s = SliderWithStopPointsStyle;
    const [componentWidth, setComponentWidth] = useState(null);
    const xPosition = useRef(new Animated.Value(0)).current;
    const [hasLayout, setHasLayout] = useState(false);
    const [showHover, setShowHover] = useState(false);
    const [hoverValue, setHoverValue] = useState(value!==undefined ? value : 1);
    const [hasChangedValue, setHasChangedValue] = useState(false);
    const previousValue = useRef(value);
    const barRef = useRef<HTMLDivElement>(null);
    const sliderAriaLabel = useTranslation('Use left & right or up & down arrow keys to adjust slider');

    const directPress = useRef(false)

    function getSpaceBetweenStops() {
        const rect = barRef.current?.getBoundingClientRect();
        // 8px seems to be from the 4px of padding before the first and after the last stop point
        return (rect?.width ?? 0 - 8) / (numberOfStops - 1);
    }

    function valueToXPosition(value) {
        const stopWidth = getSpaceBetweenStops();
        return (value - 1) * stopWidth - 10;
    }

    function screenXPositionXOffset(xPosition) {
        const rect = barRef.current?.getBoundingClientRect();
        return xPosition - (rect?.left ?? 0);
    }

    function xPositionToValue(xPosition) {
        const stopWidth = getSpaceBetweenStops();
        const unclippedValue = Math.round((xPosition + 10) / stopWidth) + 1;
        return Math.max(1, Math.min(numberOfStops, unclippedValue));
    }
    useEffect(()=>{
        if(value!==3 && previousValue.current!==3){
            setShowHover(true)
        }
    },[])

    useEffect(() => {
        const newXPosition = valueToXPosition(value);
        xPosition.flattenOffset();

        if (previousValue.current > 0 && previousValue.current != value) {
            Animated.timing(xPosition, {
                toValue: newXPosition, duration: 300, useNativeDriver: false, 
            }).start();
        } else {
            xPosition.setValue(newXPosition);
        }
        previousValue.current = value;
    }, [value, componentWidth]);
    
    useEffect(() => {
        // Moving the handle with arrow keys instead of dragging it
        const handleKeyDown = ({ key }) => {
            if (document.activeElement?.id !== barRef?.current?.id) return null;

            onStartInteraction();

            let newHoverValue: number = 3;

            switch (key) {
                case "ArrowLeft":
                    newHoverValue = Math.max(1, value - 1);
                    break;
                case "ArrowRight":
                    newHoverValue = Math.min(numberOfStops, value + 1);
                    break;
                case "ArrowDown":
                    newHoverValue = Math.max(1, value - 1);
                    break;
                case "ArrowUp":
                    newHoverValue = Math.min(numberOfStops, value + 1);
                    break;
                default:
                    return
            }

            setHoverValue(newHoverValue);
            onChangeValue(newHoverValue);
            setHasChangedValue(true);
            setShowHover(true);
        };
        window.addEventListener('keydown', handleKeyDown);
        
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [value, onChangeValue, numberOfStops]);

    // Moving the handle by dragging
    const panResponder = PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: () => {
            xPosition.flattenOffset();
        },
        onPanResponderMove: (_, gestureState) => {
            onStartInteraction?.();
            const dragX = screenXPositionXOffset(gestureState.moveX) - 10;
            const rect = barRef.current?.getBoundingClientRect();
            const clippedX = Math.max(-10, Math.min(rect?.width ?? 0 - 10, dragX));
            xPosition.setValue(clippedX);
            setHasChangedValue(true);
            setShowHover(true);
            const newHoverValue = xPositionToValue(dragX);
            setHoverValue(newHoverValue);
        },
        onPanResponderRelease: (_, gestureState) => {
            onEndInteraction?.();
            xPosition.flattenOffset();
            const pressX = screenXPositionXOffset(gestureState.moveX);
            const newValue = xPositionToValue(pressX);
            const newXPosition = valueToXPosition(newValue);
            Animated.timing(xPosition, {
                toValue: newXPosition,
                duration: 300,
                useNativeDriver: false,
            }).start();
            onChangeValue(newValue);
        },
    });
  
    // Moving the handle by clicking into slider instead of dragging the handle
    const handleBarPress = (e) => {
        if ((componentWidth ?? 0) > 0 && barRef.current) {
            const pressX = screenXPositionXOffset(e.nativeEvent.pageX);
            const newValue = xPositionToValue(pressX);
            setHoverValue(newValue);
            setHasChangedValue(true);
            if(directPress.current) {
                directPress.current = false;
                onChangeValue(newValue);
            }
        }
    };
    
    const handleLayout = (event) => {
        const {width} = event.nativeEvent.layout;
        setComponentWidth(width);
        xPosition.setValue((value - 1) * (width / (numberOfStops - 1)) - 16);
        setHasLayout(true);
    };

    const onPressIn = () => {
        onStartInteraction?.()
        directPress.current = true
    }

    return (
        <View>
            <DataVizText type="small" label={title} />
            <Pressable
                onPress={handleBarPress}
                onPressIn={onPressIn}
                ref={barRef as any}
                id={`barRef-${title.replace(/\s+/g, "-").toLowerCase()}`}
                aria-label={sliderAriaLabel}
            >
                <View style={s.container} onLayout={handleLayout}>
                    {value > 0 && hasLayout && (
                        <Animated.View
                            {...panResponder.panHandlers}
                            style={[
                                s.dragHandle,
                                { backgroundColor: initial ? colorButtonSecondary : colorButtonAccent },
                                { transform: [{ translateX: xPosition }] },
                                showHover ? s.dragHandleShadow : null,
                            ]}
                        >
                            {(!initial) && (
                                // TODO: The text and the "You" tag are always slightly off-center
                                <View
                                    style={[
                                        s.overlapBar,
                                        {
                                            left: xPosition,
                                            transform: [{ translateX: hoverValue == 1 ? "50%" : hoverValue === numberOfStops ? "-50%" : "0%" }] as any,
                                        },
                                    ]}
                                >
                                    <Pad size={10} />
                                    <HoverHelpBar label={optionLabels[hoverValue - 1]} />
                                </View>
                            )}
                        </Animated.View>
                    )}
                    <PadBox horiz={4}>
                        <HorizBox spread center>
                            {Array(numberOfStops)
                                .fill(null)
                                .map((_, idx) => (
                                    <View key={idx} style={s.selectionStopPoint} />
                                ))}
                        </HorizBox>
                    </PadBox>
                </View>
            </Pressable>
        </View>
    );
};
  
const SliderWithStopPointsStyle = StyleSheet.create({
    container: {
        width: '100%',
        borderRadius: 4,
        marginVertical: 16,
        backgroundColor: colorGreyHover,
        justifyContent: 'center',
    },
    overlapBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    dragHandle: {
        width: 24,
        height: 24,
        borderRadius: 16,
        position: 'absolute',
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        cursor: 'pointer',
        zIndex: 10,
        alignItems: 'center',
        justifyContent: 'center',
    } as any,
    dragHandleShadow: {
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.30)',
    } as any,
    selectionStopPoint: {
        width: 6,
        height: 6,
        borderRadius: 4,
        backgroundColor: colorAmbiguousWhite,
        borderColor: colorSurfaceSecondary,
    },
});

// TODO: Consider renaming this. Calling this "hover" something doesn't fit, this isn't something that appears on hover
function HoverHelpBar({ label }) {
    const s = HoverHelpBarStyle;
    if (!label || label.length === 0) return;
    return (
        <View style={s.outer}>
            <NonSelectableView>
               <View style={[s.hoverPill]}>
                    <View style={s.textContainer}>
                        <Pad size={8} />
                        <UtilityText
                            weight="medium"
                            type="tiny"
                            label={label}
                            color={colorButtonAccent}
                            isNonSelectable={true}
                        />
                        <View style={s.youTag}>
                            <UtilityText
                                label={t("You")}
                                color={colorButtonSecondary}
                                center
                                isNonSelectable={true}
                            />
                        </View>
                    </View>
                </View> 
            </NonSelectableView>
        </View>
    );
}

const HoverHelpBarStyle = StyleSheet.create({
    label: {
        color: colorAmbiguousWhite,
    },
    outer: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    hoverPill: {
        backgroundColor: "transparent",
        borderRadius: 8,
        color: colorButtonAccent,
        paddingTop: 8,
        flexDirection: "row",
        alignItems: "center",
        flexGrow: 0,
        flexShrink: 1,
        alignSelf: "flex-start",
    },
    textContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
    },
    youTag: {
        borderRadius: 16,
        width: 37,
        height: 30,
        backgroundColor: colorButtonAccent,
        padding: 6,
    },
});